import type { Ref } from 'react';
import * as React from 'react';
import { forwardRef } from 'react';
import type { AutocompleteProps } from '@components/moleculs/autocomplete/types';
import type { AutocompleteOption } from '@models/types';
import type { AutocompleteRenderValueGetItemProps } from '@mui/material/Autocomplete';
import MuiAutocomplete from '@mui/material/Autocomplete';
import type { PopperProps } from '@mui/material/Popper';
import { sameWidthModifier } from '@utils/popup-modifiers';
import cn from 'classnames';

import { ArrowIcon } from '../../icons';
import { Chip } from '../chip';
import { Popper } from '../popper';

import { AutocompleteFormControl } from './styles';

const AutocompleteComponent = forwardRef(
    <
        T,
        Multiple extends boolean | undefined = false,
        DisableClearable extends boolean | undefined = false,
        FreeSolo extends boolean | undefined = false,
    >(
        props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
        ref: Ref<HTMLSelectElement>,
    ) => {
        const { fullWidth, className, popupIcon = <ArrowIcon />, renderValue, ...rest } = props;

        // In MUI v7 the chip rendering lived in `renderTags`, which fired ONLY for `multiple`
        // Autocompletes (value was always an array). MUI v9 replaced it with `renderValue`,
        // which ALSO fires for single-select (value = one option) and turns the returned node
        // into the input's `startAdornment` — returning a node there, or even `null`, detaches
        // the native <input> ("Unable to find the input element" + `focus()` on null). So we
        // mirror the old semantics: supply this chip renderer only for `multiple`, and leave
        // single-select to MUI's default input-text display (no `renderValue` at all).
        const renderMultipleValue: NonNullable<typeof renderValue> = (value, getItemProps) =>
            // Wired up only for `multiple` (see below), where MUI always passes an array of
            // options; the generic `renderValue` value type can't be narrowed to that here.
            (value as Array<AutocompleteOption<T>>).map((opt, index) => {
                // getItemProps returns `key` at runtime, but the type only exposes it for the
                // `Multiple extends true` branch (here `Multiple` is generic). This renderer only
                // runs for `multiple`, so read via the `<true>` getter shape and pass `key`
                // explicitly — spreading it into JSX wouldn't apply it (key-spread warning).
                const { key, ...chipProps } = (
                    getItemProps as AutocompleteRenderValueGetItemProps<true>
                )({ index });

                return <Chip key={key} {...chipProps} label={opt.label} />;
            });

        return (
            <AutocompleteFormControl variant='standard' fullWidth={fullWidth} className={className}>
                <MuiAutocomplete<T, Multiple, DisableClearable, FreeSolo>
                    ref={ref}
                    {...rest}
                    renderValue={renderValue ?? (rest.multiple ? renderMultipleValue : undefined)}
                    popupIcon={popupIcon}
                    slots={{
                        popper: ({ className: popperClassName, ...popperRest }) => (
                            <Popper
                                className={cn('autocomplete-popper', popperClassName)}
                                modifiers={[sameWidthModifier] as PopperProps['modifiers']}
                                {...popperRest}
                            />
                        ),
                    }}
                />
            </AutocompleteFormControl>
        );
    },
);

export const Autocomplete = AutocompleteComponent as <
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
>(
    props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
        ref?: Ref<HTMLSelectElement>;
    },
) => React.ReactElement;
